// ═══════════════════════════════════════════════════════════════════════════════
// Shared schedule fields for pickup / medication task group wizards
// ═══════════════════════════════════════════════════════════════════════════════

import {
  MONTHLY_KIND,
  RECURRENCE_TYPE,
  type MonthlyKind,
  type RecurrenceType,
  type TaskDateRange,
} from '@fdh/shared';
import { IsoDatePicker } from '../IsoDatePicker';
import {
  HelperDueReminderCheckbox,
  type HelperDueReminderCopyContext,
} from './HelperDueReminderCheckbox';
import {
  emptyMonthlySchedulePatch,
  TaskMonthlyScheduleControls,
} from './TaskMonthlyScheduleControls';

export type TaskGroupScheduleState = {
  recurrence: RecurrenceType;
  recurrenceDays: number[];
  monthlyKind: MonthlyKind | null;
  recurrenceMonthDays: number[];
  recurrenceOrdinal: number | null;
  occurrenceDate: string;
  startDate: string;
  endDate: string;
  exclusionRanges: TaskDateRange[];
  requirePhoto: boolean;
  notifyEmployer: boolean;
  remindHelperBeforeDue: boolean;
};

type TaskGroupScheduleFieldsProps = {
  t: (key: string) => string;
  schedule: TaskGroupScheduleState;
  onScheduleChange: (patch: Partial<TaskGroupScheduleState>) => void;
  todayYmd: string;
  isEdit?: boolean;
  helperReminderCopyContext?: HelperDueReminderCopyContext;
};

const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const DAY_KEYS = [
  'task.day.sun',
  'task.day.mon',
  'task.day.tue',
  'task.day.wed',
  'task.day.thu',
  'task.day.fri',
  'task.day.sat',
] as const;

export const defaultTaskGroupScheduleState = (
  todayYmd: string,
): TaskGroupScheduleState => ({
  recurrence: RECURRENCE_TYPE.NONE,
  recurrenceDays: [],
  monthlyKind: null,
  recurrenceMonthDays: [],
  recurrenceOrdinal: null,
  occurrenceDate: todayYmd,
  startDate: todayYmd,
  endDate: '',
  exclusionRanges: [],
  requirePhoto: true,
  notifyEmployer: true,
  remindHelperBeforeDue: false,
});

export const TaskGroupScheduleFields = ({
  t,
  schedule,
  onScheduleChange,
  todayYmd,
  isEdit = false,
  helperReminderCopyContext = 'default',
}: TaskGroupScheduleFieldsProps) => {
  const showWeekdays =
    schedule.recurrence === RECURRENCE_TYPE.WEEKLY ||
    schedule.recurrence === RECURRENCE_TYPE.BIWEEKLY;
  const isMonthly = schedule.recurrence === RECURRENCE_TYPE.MONTHLY;
  const isOneTime = schedule.recurrence === RECURRENCE_TYPE.NONE;

  const handleDayToggle = (day: number) => {
    const next = schedule.recurrenceDays.includes(day)
      ? schedule.recurrenceDays.filter((d) => d !== day)
      : [...schedule.recurrenceDays, day].sort((a, b) => a - b);
    onScheduleChange({ recurrenceDays: next });
  };

  const handleRecurrenceChange = (value: RecurrenceType) => {
    if (value === RECURRENCE_TYPE.MONTHLY) {
      onScheduleChange({
        recurrence: value,
        ...emptyMonthlySchedulePatch(MONTHLY_KIND.DAY),
        exclusionRanges: schedule.exclusionRanges,
        startDate: schedule.startDate || todayYmd,
      });
      return;
    }
    onScheduleChange({
      recurrence: value,
      recurrenceDays:
        value === RECURRENCE_TYPE.WEEKLY || value === RECURRENCE_TYPE.BIWEEKLY
          ? schedule.recurrenceDays
          : [],
      monthlyKind: null,
      recurrenceMonthDays: [],
      recurrenceOrdinal: null,
      exclusionRanges: value === RECURRENCE_TYPE.NONE ? [] : schedule.exclusionRanges,
      startDate: value === RECURRENCE_TYPE.NONE ? '' : schedule.startDate || todayYmd,
    });
  };

  const handleExclusionRangeChange = (
    index: number,
    field: 'startDate' | 'endDate',
    value: string,
  ) => {
    onScheduleChange({
      exclusionRanges: schedule.exclusionRanges.map((range, rangeIndex) =>
        rangeIndex === index ? { ...range, [field]: value } : range,
      ),
    });
  };

  const handleAddExclusionRange = () => {
    onScheduleChange({
      exclusionRanges: [...schedule.exclusionRanges, { startDate: '', endDate: '' }],
    });
  };

  const handleRemoveExclusionRange = (index: number) => {
    onScheduleChange({
      exclusionRanges: schedule.exclusionRanges.filter((_, rangeIndex) => rangeIndex !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="tg-recurrence">
          {t('task.form.recurrenceLabel')}
        </label>
        <select
          id="tg-recurrence"
          value={schedule.recurrence}
          onChange={(e) => handleRecurrenceChange(e.target.value as RecurrenceType)}
          className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value={RECURRENCE_TYPE.NONE}>{t('task.recurrence.none')}</option>
          <option value={RECURRENCE_TYPE.DAILY}>{t('task.recurrence.daily')}</option>
          <option value={RECURRENCE_TYPE.WEEKLY}>{t('task.recurrence.weekly')}</option>
          <option value={RECURRENCE_TYPE.BIWEEKLY}>{t('task.recurrence.biweekly')}</option>
          <option value={RECURRENCE_TYPE.MONTHLY}>{t('task.recurrence.monthly')}</option>
        </select>
      </div>
      {isMonthly ? (
        <TaskMonthlyScheduleControls
          t={t}
          idPrefix="tg-monthly"
          monthlyKind={schedule.monthlyKind}
          recurrenceDays={schedule.recurrenceDays}
          recurrenceMonthDays={schedule.recurrenceMonthDays}
          recurrenceOrdinal={schedule.recurrenceOrdinal}
          onChange={(patch) => onScheduleChange(patch)}
        />
      ) : null}
      {showWeekdays ? (
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('task.form.daysLabel')}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => handleDayToggle(day)}
                className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  schedule.recurrenceDays.includes(day)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
                }`}
                aria-pressed={schedule.recurrenceDays.includes(day)}
              >
                {t(DAY_KEYS[day])}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {isOneTime ? (
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="tg-occurrence">
            {t('task.form.occurrenceDateLabel')}
          </label>
          <IsoDatePicker
            id="tg-occurrence"
            ariaLabel={t('task.form.occurrenceDateLabel')}
            value={schedule.occurrenceDate}
            minDate={isEdit ? undefined : todayYmd}
            onChange={(next) => onScheduleChange({ occurrenceDate: next })}
            valueFormat="iso"
            className="mt-1 w-full"
          />
        </div>
      ) : (
        <>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="tg-start">
              {t('task.form.startDateLabel')}
            </label>
            <IsoDatePicker
              id="tg-start"
              ariaLabel={t('task.form.startDateLabel')}
              value={schedule.startDate}
              maxDate={schedule.endDate || undefined}
              onChange={(next) => onScheduleChange({ startDate: next })}
              valueFormat="iso"
              className="mt-1 w-full"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor="tg-end">
              {t('task.form.endDateLabelOptional')}
            </label>
            <IsoDatePicker
              id="tg-end"
              ariaLabel={t('task.form.endDateLabelOptional')}
              value={schedule.endDate}
              minDate={schedule.startDate || undefined}
              onChange={(next) => onScheduleChange({ endDate: next })}
              valueFormat="iso"
              className="mt-1 w-full"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('task.form.excludeRangesLabel')}
              </label>
              <button
                type="button"
                onClick={handleAddExclusionRange}
                className="rounded-lg border border-primary-300 bg-white px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-700 dark:bg-gray-900 dark:text-primary-300 dark:hover:bg-primary-950/40"
              >
                {t('task.form.addExcludeRange')}
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {schedule.exclusionRanges.map((range, index) => (
                <div
                  key={`tg-exclude-range-${index}`}
                  className="rounded-lg border border-gray-200 p-3 dark:border-gray-600"
                >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <IsoDatePicker
                      id={`tg-exclude-start-${index}`}
                      ariaLabel={t('task.form.excludeStartDateLabel')}
                      value={range.startDate}
                      maxDate={range.endDate || undefined}
                      onChange={(next) => handleExclusionRangeChange(index, 'startDate', next)}
                      valueFormat="iso"
                    />
                    <IsoDatePicker
                      id={`tg-exclude-end-${index}`}
                      ariaLabel={t('task.form.excludeEndDateLabel')}
                      value={range.endDate}
                      minDate={range.startDate || undefined}
                      onChange={(next) => handleExclusionRangeChange(index, 'endDate', next)}
                      valueFormat="iso"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveExclusionRange(index)}
                    className="mt-2 rounded-lg border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-800 dark:bg-gray-900 dark:text-red-300 dark:hover:bg-red-950/40"
                  >
                    {t('task.form.removeExcludeRange')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <label className="flex min-h-11 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={schedule.requirePhoto}
          onChange={(e) => onScheduleChange({ requirePhoto: e.target.checked })}
          className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-800 dark:text-gray-100">{t('task.form.requirePhotoLabel')}</span>
      </label>
      <label className="flex min-h-11 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={schedule.notifyEmployer}
          onChange={(e) => onScheduleChange({ notifyEmployer: e.target.checked })}
          className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-800 dark:text-gray-100">{t('task.form.notifyEmployerLabel')}</span>
      </label>
      <HelperDueReminderCheckbox
        id="task-group-remind-helper"
        variant="wizard"
        copyContext={helperReminderCopyContext}
        checked={schedule.remindHelperBeforeDue}
        onChange={(remindHelperBeforeDue) => onScheduleChange({ remindHelperBeforeDue })}
        t={t}
      />
    </div>
  );
};
